import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';
import { ThemeProvider } from 'styled-components';
import nebulaTheme from '@nebula/theme';
import GlobalStyles from '@nebula/global-styles';
import { Route } from 'react-router';
import { useLocation } from 'react-router-dom';

import { fulfillmentStatuses, stateVariables } from '../../../constants';
import queryParams from '../../../constants/queryParams';
import splitsList from '../../../constants/splits';
import { FAMILIES_NOT_REQUIRING_PAYMENT } from '../../../constants/payment';
import ProcessingSpinner from '../../../components/PaymentProcessing/ProcessingSpinner';
import PaymentOrchestrator from '../../../components/PaymentOrchestrator';
import useCurrentOffer from '../../../hooks/useCurrentOffer';
import useTargetOffer from '../../../hooks/useTargetOffer';
import { trackSwitchRejected } from '../../../helpers/switch/analytics';
import usePageView from '../../../hooks/usePageView';
import trackStartUpsell from '../../../actions/upsellstarttrackerservice/trackStartUpsell';
import startSecureMode from '../../../actions/app/startSecureMode';
import SwitchIndex from '..';
import useSwitchLoadRequirements from './useSwitchLoadRequirements';
import usePaymentPageLoadAnalytics from '../../../hooks/payment/usePaymentPageLoadAnalytics';
import ChangeBillingPeriodPage from './ChangeBillingPeriodPage';
import {
  getFlexUpgradePlan,
  getFlexPlanByFamilyAndPeriod,
  getFamilyBillingPeriods,
} from '../../../helpers/flex-helpers/findFlexPlanByFamily';
import {
  useGetEntitlementsQuery,
  useGetAllProductsQuery,
  useGetIsFlexUserQuery,
} from '../../../services/entitlementsApi';

/*
    We're planning to incrementally import (and improve within reason) aspects of the existing Switch page as we build out the new provider flow from this page.
    This approach has been chosen to minimise the amount of branching we need to do within an already complex switch page. This does increase risk of keeping changes
    there in sync - but it has been asked that work in the repository is kept to a minimum.

    Regression testing is planned for all product movement journeys as they are supported here.
*/

const PAGE_NAME = 'ProdMoveSwitchPage';

const WebSwitchPage = () => {
  const dispatch = useDispatch();
  const isNewDesignEnabled = useSelector(state => state.split?.treatments?.[splitsList.prodmoveNewDesign]);
  const location = useLocation();
  const parameterProvidedFamily = new URLSearchParams(location.search).get('switchingTo');
  const rawChangeBillingPeriod = new URLSearchParams(location.search).get(queryParams.billingPeriod);
  const changeBillingPeriod = ['monthly', 'annual'].includes(rawChangeBillingPeriod) ? rawChangeBillingPeriod : null;
  const isUrlParamFlow = Boolean(parameterProvidedFamily) && isNewDesignEnabled;
  const isFlexUrlFlow = isNewDesignEnabled && (isUrlParamFlow || Boolean(changeBillingPeriod));
  const { data: isFlexUserData } = useGetIsFlexUserQuery(undefined, { skip: !isFlexUrlFlow });
  const isFlexFlow = isFlexUrlFlow && isFlexUserData?.isFlex === true;

  const { data: entitlementsData } = useGetEntitlementsQuery(undefined, {
    skip: !isFlexFlow,
  });
  const { data: allProductsData } = useGetAllProductsQuery(undefined, {
    skip: !isFlexFlow,
  });
  const shouldRejectFlexSwitch =
    isFlexFlow &&
    Boolean(entitlementsData) &&
    (!!entitlementsData.defermentInfo || entitlementsData.fulfillmentStatus !== fulfillmentStatuses.succeeded);

  const isAnnualPricingEnabled = useSelector(state => state.split?.treatments?.[splitsList.annualPricing]);
  const { hasBothBillingPeriods } = getFamilyBillingPeriods(parameterProvidedFamily, allProductsData?.eligiblePlans);
  // Deep-linked billing-period changes must satisfy the same guard as the ManageSubscriptionNav card
  const shouldRejectBillingPeriodChange =
    Boolean(changeBillingPeriod) &&
    (!isNewDesignEnabled || !isAnnualPricingEnabled || (Boolean(allProductsData) && !hasBothBillingPeriods));

  // Billing period changes stay within the same family, so the plan must be matched on term rather than family alone
  const upgradePlan = (() => {
    if (!isFlexFlow) return null;

    return changeBillingPeriod
      ? getFlexPlanByFamilyAndPeriod(
          parameterProvidedFamily,
          changeBillingPeriod,
          allProductsData?.eligiblePlans,
          allProductsData?.isTrialEligible
        )
      : getFlexUpgradePlan(parameterProvidedFamily, allProductsData?.eligiblePlans, allProductsData?.isTrialEligible);
  })();

  const capturedPlanPriceRef = useRef(null);
  const livePlanPrice = entitlementsData?.planPrice ?? entitlementsData?.plans?.[0]?.price;
  if (capturedPlanPriceRef.current == null && typeof livePlanPrice === 'number') {
    capturedPlanPriceRef.current = livePlanPrice;
  }

  const currentFlexSubscription = useMemo(() => {
    if (!isFlexUrlFlow) {
      return null;
    }

    const termFrequency = entitlementsData?.termFrequency ?? entitlementsData?.plans?.[0]?.termFrequency;

    return {
      family: entitlementsData?.family,
      planCode: entitlementsData?.plans?.[0]?.planCode,
      freeTrial: Boolean(entitlementsData?.plans?.[0]?.trialLength),
      ...(termFrequency == null ? {} : { termFrequency }),
      ...(typeof capturedPlanPriceRef.current === 'number' ? { price: capturedPlanPriceRef.current } : {}),
    };
  }, [isFlexUrlFlow, entitlementsData, allProductsData]);

  const currentOffer = useCurrentOffer();
  const { targetOffer, targetFamily, isSourcedFromUrl, isTrial } = useTargetOffer();
  const flexProduct = upgradePlan?.product;
  // Free families (basic/breach/breach_plus) are voucher-redeemed and never appear in the Flex paid
  // eligiblePlans catalog, so Flex users must resolve them the same way non-Flex users do rather than
  // via getFlexUpgradePlan (which would always return null and fail switchCanLoad below).
  const rawTargetFamily = isFlexUrlFlow ? parameterProvidedFamily : undefined;
  const isFreeFamilyTarget = FAMILIES_NOT_REQUIRING_PAYMENT.includes(rawTargetFamily);
  const isFlexPaidFlow = isFlexFlow && !isFreeFamilyTarget;
  const resolvedTargetOffer = isFlexPaidFlow
    ? {
        id: flexProduct?.planCode,
        planCode: flexProduct?.planCode,
        family: flexProduct?.family ?? upgradePlan?.family,
        name: flexProduct?.name,
        descriptors: [],
        terms: [
          {
            price: flexProduct?.price,
            billingFrequency: ['annual', 'annually'].includes(flexProduct?.term?.toLowerCase?.())
              ? 'annual'
              : 'monthly',
          },
        ],
      }
    : targetOffer;
  const resolvedTargetFamily = isFlexPaidFlow ? flexProduct?.family ?? upgradePlan?.family : targetFamily;
  const resolvedIsTrial = isFlexPaidFlow ? (flexProduct?.trialLength ?? 0) > 0 : isTrial;

  const onReject = () =>
    dispatch(
      push('/', {
        [stateVariables.persistErrorsOnLocationChange]: true,
      })
    );

  const { switchDepsLoading, switchCanLoad, switchDepsError } = useSwitchLoadRequirements(
    resolvedTargetOffer,
    currentOffer,
    onReject
  );

  usePageView(PAGE_NAME, {
    productChange: { switchingTo: resolvedTargetFamily, isTrial: resolvedIsTrial, fromDeeplink: isSourcedFromUrl },
  });

  useEffect(() => {
    if (!changeBillingPeriod) {
      dispatch(trackStartUpsell(resolvedTargetFamily, resolvedIsTrial));
    }
    dispatch(startSecureMode());
  }, [dispatch, resolvedTargetFamily, resolvedIsTrial, changeBillingPeriod]);

  usePaymentPageLoadAnalytics(resolvedTargetOffer, FAMILIES_NOT_REQUIRING_PAYMENT, isFlexFlow);

  useEffect(() => {
    if (shouldRejectBillingPeriodChange || (!changeBillingPeriod && (!switchCanLoad || shouldRejectFlexSwitch))) {
      trackSwitchRejected();
      dispatch(
        push('/', {
          [stateVariables.persistErrorsOnLocationChange]: true,
        })
      );
    }
  }, [switchCanLoad, shouldRejectFlexSwitch, shouldRejectBillingPeriodChange, dispatch, changeBillingPeriod]);

  // For billing period changes, skip normal switch validation (same family is valid)
  if (changeBillingPeriod) {
    return shouldRejectBillingPeriodChange ? null : (
      <ChangeBillingPeriodPage
        upgradePlan={upgradePlan}
        targetPeriod={changeBillingPeriod}
        isFlexFlow={isFlexFlow}
        currentFlexSubscription={currentFlexSubscription}
      />
    );
  }

  if (!switchCanLoad || switchDepsError || shouldRejectFlexSwitch) {
    return null;
  }

  /*
    fallback to the old switch page for any product change not requiring payment (free, breach), these
    will be extracted post-migration
  */
  if (FAMILIES_NOT_REQUIRING_PAYMENT.includes(resolvedTargetOffer.family)) {
    return <Route component={SwitchIndex} />;
  }

  return (
    <ThemeProvider theme={nebulaTheme}>
      <GlobalStyles />
      {switchDepsLoading ? (
        <ProcessingSpinner isWebview={false} />
      ) : (
        <PaymentOrchestrator
          upgradeOffer={isFlexFlow ? null : targetOffer}
          upgradePlan={isFlexFlow ? upgradePlan : null}
          currentFlexSubscription={isFlexFlow ? currentFlexSubscription : null}
          isTrial={resolvedIsTrial}
          isFlexFlow={isFlexFlow}
        />
      )}
    </ThemeProvider>
  );
};

export default WebSwitchPage;
